/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { PaginatorPageState } from 'primereact/paginator';
import { Column, ColumnEditorOptions } from 'primereact/column';
import {
	DataTable,
	DataTablePFSEvent,
	DataTableSortOrderType
} from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';

import './ProductDetails.scss';

import Filters from './filters/Filters';
import AddProductDetail from './add/AddProductDetail';
import Pagination from '../common/pagination/Pagination';
import {
	ProductDetailsListingDivision,
	ProductDetailsListingResponseModel
} from '../../models/productDetails/ProductDetailsListingResponseModel';
import { ProductDetailsSearchResponseModel } from '../../models/productDetails/ProductDetailsSearchResponseModel';
import { HttpService, IHttpService } from '../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../services/notifierService';
import { nameof } from '../../shared/utility';
import EditProductDetail from './edit/EditProductDetail';
import MoveProductDetail from './move/MoveProductDetail';

const ProductDetails = (): JSX.Element => {
	const [loading, setLoading] = useState<boolean>(false);
	const [first, setFirst] = useState<number>(0);
	const [rows, setRows] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortField, setSortField] = useState<string>('');
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>();
	const [descriptionFilter, setDescriptionFilter] = useState<string>('');
	const [divisionFilter, setDivisionFilter] = useState<string>('');
	const [productTypeFilter, setProductTypeFilter] = useState<string>('');
	const [shouldReload, setShouldReload] = useState<boolean>(false);
	const [productDetailsSearch, setProductDetailsSearch] =
		useState<ProductDetailsSearchResponseModel>();

	const [productDetailsListingBackup, setProductDetailsListingBackup] =
		useState<ProductDetailsListingResponseModel[]>([]);
	const [productDetailsListing, setProductDetailsListing] = useState<
		ProductDetailsListingResponseModel[]
	>([]);
	const [expandedRows, setExpandedRows] = useState<
		ProductDetailsListingResponseModel[] | undefined
	>([]);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const fetchProductDetails = async (): Promise<
		ProductDetailsListingResponseModel[]
	> => {
		setLoading(true);

		let result: ProductDetailsListingResponseModel[] = [];
		let order = '';
		if (sortOrder) {
			order = sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
		}
		await httpService
			.get<ProductDetailsSearchResponseModel>(
				`productDetails?page=${currentPage}&description=${descriptionFilter}&divisionDescription=${divisionFilter}&productTypeCode=${productTypeFilter}&sortBy=${sortField}&order=${order}`,
				undefined
			)
			.then((productDetails) => {
				setProductDetailsSearch(productDetails);
				setProductDetailsListing(productDetails.models);
				setShouldReload(false);
				setLoading(false);

				result = productDetails.models;
			})
			.catch((err) => {
				setShouldReload(false);
				setLoading(false);
				notifierService.error(err);

				setProductDetailsListing(productDetailsListingBackup);
				result = [];
			});

		return result;
	};

	useEffect(() => {
		fetchProductDetails();
	}, [
		currentPage,
		sortField,
		sortOrder,
		descriptionFilter,
		divisionFilter,
		productTypeFilter
	]);

	useEffect(() => {
		if (shouldReload == true) {
			fetchProductDetails();
		}
	}, [shouldReload]);

	const searchTree = useCallback(
		(
			tree: ProductDetailsListingResponseModel,
			matchingId: number
		): ProductDetailsListingResponseModel | null => {
			if (tree.id === matchingId) {
				return tree;
			} else if (tree.productDetails != null) {
				var result = null;
				for (
					let i = 0;
					result == null && i < tree.productDetails.length;
					i++
				) {
					result = searchTree(tree.productDetails[i], matchingId);
				}
				return result;
			}
			return null;
		},
		[]
	);

	const findProductDetail = useCallback(
		(
			listing: ProductDetailsListingResponseModel[],
			matchingId: number
		): ProductDetailsListingResponseModel | null => {
			let result = null;
			for (let i = 0; result == null && i < listing.length; i++) {
				result = searchTree(listing[i], matchingId);
			}
			return result;
		},
		[searchTree]
	);

	const handleInputTextChange = useCallback(
		(
			id: number,
			field: keyof ProductDetailsListingResponseModel,
			value: any
		) => {
			const newProductDetails: ProductDetailsListingResponseModel[] = [
				...productDetailsListing
			];
			const productDetail = findProductDetail(productDetailsListing, id);

			if (productDetail) {
				(productDetail as any)[field] = value;
				setProductDetailsListing(newProductDetails);
			}
		},
		[productDetailsListing, setProductDetailsListing, findProductDetail]
	);

	const handleCellEditor = useCallback(
		(
			cellData: ColumnEditorOptions,
			field: keyof ProductDetailsListingResponseModel
		) => {
			const cellValue = (cellData.rowData[field] as string) || '';
			return (
				<InputText
					value={cellValue}
					onChange={(e: ChangeEvent<HTMLInputElement>) => {
						//@ts-ignore
						cellData.editorCallback(e.target.value);
						handleInputTextChange(
							cellData.rowData.id,
							field,
							e.target.value
						);
					}}
				/>
			);
		},
		[handleInputTextChange]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleInputSwitchChange = async (
		e: InputSwitchChangeParams,
		productDetailId: number,
		divisionId: number
	) => {
		const requestJson = JSON.stringify({
			productDetailId,
			divisionId
		});

		try {
			await httpService.put(
				`ProductDetails/ChangeActive`,
				requestJson,
				undefined,
				false
			);

			let order = '';
			if (sortOrder) {
				order =
					sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
			}

			const brands =
				await httpService.get<ProductDetailsSearchResponseModel>(
					`productDetails?page=${currentPage}&description=${descriptionFilter}&divisionCode=${divisionFilter}&productTypeCode=${productTypeFilter}&sortBy=${sortField}&order=${order}`,
					undefined
				);

			setProductDetailsListing(brands.models);
		} catch (err) {
			notifierService.error(err);
			setShouldReload(true);
		}
	};

	const onRenderDivisions = useCallback(
		(
			productDetailId: number,
			divisions: ProductDetailsListingDivision[]
		) => {
			return divisions.map((division) => (
				<div
					className='product-details__datatable-status'
					key={division.id}
				>
					<InputSwitch
						id={division.id.toString()}
						checked={division.isActive}
						onChange={(e) =>
							handleInputSwitchChange(
								e,
								productDetailId,
								division.id
							)
						}
						tooltip='Change active status'
						tooltipOptions={{ position: 'left' }}
					/>
					{division.description}
					<br />
				</div>
			));
		},
		[handleInputSwitchChange]
	);

	const handleEditorSubmit = async (e: any) => {
		const { rowData } = e;
		await httpService
			.putGeneric<number[]>(
				`productDetails/${rowData.id}`,
				JSON.stringify(rowData),
				undefined,
				false
			)
			.then(async (response: number[]) => {
				await fetchProductDetails();
			})
			.catch((err) => {
				notifierService.error(err);
				setShouldReload(true);
			});
	};

	const getAddHeader = (
		parentId?: number | null,
		parentDescription?: string | null
	) => (
		<AddProductDetail
			parentProductDetailId={parentId}
			parentProductDetailDescription={parentDescription}
			setShouldReload={setShouldReload}
		/>
	);

	const onRenderMoveTo = (product: ProductDetailsListingResponseModel) => {
		return (
			<>
				<MoveProductDetail
					setShouldReload={setShouldReload}
					productName={product.description}
					productId={product.id}
					productDetailsListing={productDetailsListing}
					fetchProductDetails={fetchProductDetails}
				/>
				<EditProductDetail
					setShouldReload={setShouldReload}
					productName={product.description}
					productId={product.id}
					currentDivisions={product.divisions}
				/>
			</>
		);
	};

	const onPageChange = (event: PaginatorPageState) => {
		setFirst(event.first);
		setRows(event.rows);
		setCurrentPage(event.page! + 1);
	};

	const onSort = (event: DataTablePFSEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	const rowExpansionTemplate = (data: ProductDetailsListingResponseModel) => {
		return (
			<DataTable
				value={data.productDetails}
				responsiveLayout='scroll'
				expandedRows={expandedRows}
				dataKey='id'
				onRowToggle={(e: any) => setExpandedRows(e.data)}
				rowExpansionTemplate={rowExpansionTemplate}
			>
				<Column expander style={{ width: '3em' }} />

				<Column
					sortable
					header='Order'
					field={nameof<ProductDetailsListingResponseModel>('order')}
					editor={(ep) => handleCellEditor(ep, 'order')}
					onCellEditComplete={handleEditorSubmit}
				/>

				<Column
					sortable
					header='Description'
					field={nameof<ProductDetailsListingResponseModel>(
						'description'
					)}
					editor={(ep) => handleCellEditor(ep, 'description')}
					onCellEditComplete={handleEditorSubmit}
				/>

				<Column
					sortable
					header='Product type description'
					field={nameof<ProductDetailsListingResponseModel>(
						'productTypeDescription'
					)}
				/>

				<Column
					sortable
					header='Divisions'
					body={(productData: ProductDetailsListingResponseModel) =>
						onRenderDivisions(productData.id, productData.divisions)
					}
				/>

				<Column
					header={getAddHeader(data.id, data.description)}
					bodyClassName='product-details__actions'
					body={(productData: ProductDetailsListingResponseModel) =>
						onRenderMoveTo(productData)
					}
				/>
			</DataTable>
		);
	};

	return (
		<>
			<Filters
				setDescriptionFilter={setDescriptionFilter}
				setDivisionFilter={setDivisionFilter}
				setProductTypeFilter={setProductTypeFilter}
				setShouldReload={setShouldReload}
			/>

			<DataTable
				value={productDetailsListing}
				responsiveLayout='scroll'
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				dataKey='id'
				expandedRows={expandedRows}
				onRowToggle={(e: any) => setExpandedRows(e.data)}
				rowExpansionTemplate={rowExpansionTemplate}
				loading={loading}
			>
				<Column expander style={{ width: '3em' }} />

				<Column
					sortable
					header='Order'
					field={nameof<ProductDetailsListingResponseModel>('order')}
					editor={(ep) => handleCellEditor(ep, 'order')}
					onCellEditComplete={handleEditorSubmit}
				/>

				<Column
					sortable
					header='Description'
					field={nameof<ProductDetailsListingResponseModel>(
						'description'
					)}
					editor={(ep) => handleCellEditor(ep, 'description')}
					onCellEditComplete={handleEditorSubmit}
				/>

				<Column
					sortable
					header='Product type description'
					field={nameof<ProductDetailsListingResponseModel>(
						'productTypeDescription'
					)}
				/>

				<Column
					sortable
					header='Divisions'
					body={(productData: ProductDetailsListingResponseModel) =>
						onRenderDivisions(productData.id, productData.divisions)
					}
				/>

				<Column
					header=''
					bodyClassName='product-details__actions'
					body={(productData: ProductDetailsListingResponseModel) =>
						onRenderMoveTo(productData)
					}
				/>
			</DataTable>

			<Pagination
				totalRecords={productDetailsSearch?.total}
				rows={rows}
				first={first}
				onPageChange={onPageChange}
			/>
		</>
	);
};

export default ProductDetails;
